/* eslint-disable camelcase */
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Carousel } from 'antd';
import { makeStyles, MenuItem } from '@material-ui/core';
import { isCurrentProjectBuildy } from 'src/util/projectCheckers';
import { getAllCategories } from 'src/util/getAllCategories';
import Modal from '../../component/Modal/Modal';
import CButton from '../../component/Button';
import { setItemAddressDraft, setItemDraft, setItemsImagesDraft } from '../../actions/ItemAction';
import { CStandardInput } from '../../component/Input';
import { DefaultSelect } from '../../component/Select';

const useStyles = makeStyles({
  carousel: {
    gridColumn: '3',
    gridRow: '1 / span 5',
    maxWidth: '230px',
    textAlign: 'start'
  },
  carouselCancel: {
    cursor: 'pointer',
    fontSize: '20px',
    position: 'absolute',
    right: '10px',
    top: '6px'
  },
  carouselImage: {
    maxHeight: '100%',
    maxWidth: '100%'
  },
  carouselWrapper: {
    maxHeight: '340px',
    position: 'relative',
    width: '100%'
  },
  dropdownStyle: {
    border: '1px solid #CAD8FF',
    borderRadius: '0px 0px 14px 14px',
    fontFamily: 'Poppins',
    padding: '0px',
    transform: 'translateY(5px) !important',
    width: '32px'
  },
  form: {
    flexDirection: 'row',
    height: '100%',
    justifyContent: 'space-between'
  },
  gridContainer: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '35% 35% 30%',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridItem: {
    maxWidth: '90%',
    textAlign: 'start'
  },
  input: {
    '& .MuiInputBase-root': {
      background: 'red',
      border: 'none'
    }
  },
  p: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%'
  },
  root: {
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },
    '& .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      fontFamily: 'Poppins',
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  select: {
    '& .MuiSelect-root': {
      padding: '9px'
    },
    borderRadius: '10px',
    marginLeft: '8px',
    maxHeight: '40px ',
    padding: '0px',
    width: '150px'
  },
  selectHeader: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%',
    margin: '0px',
    transform: 'translate(-30px, -5px) scale(0.83)'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500
  },
  wrapperDiv: {
    display: 'flex',
    flexDirection: 'column',
    height: '450px',
    justifyContent: 'space-evenly'
  }
});

const dropdownSelect = {
  marginRight: '16px',
  marginTop: '5px'
};

const ItemDetailEdit = forwardRef(
  (
    {
      selectedCategory,
      subCategories,
      selectedImages,
      deletedImagesIds,
      setDeletedImagesIds,
      onUpdate,
      secondLevelCategories
    },
    ref
  ) => {
    const itemDraft = useSelector(({ item }) => item.itemDraft);
    const itemAddressDraft = useSelector(({ item }) => item.itemAddressDraft);
    const [itemDraftState, setItemDraftState] = useState(itemDraft);
    if (!itemDraft) {
      return;
    }
    const { title, price, brand, gender, condition, weight, address } = itemDraft;
    const stimullFee = itemDraft.metadata ? itemDraft.metadata.stimullFee : undefined;
    const classes = useStyles();
    const dispatch = useDispatch();
    const categories = useSelector(({ item }) => item.categories);

    const [openModal, setOpenModal] = useState(false);

    const [filteredSubCategories, setFilteredSubCategories] = useState(() => {
      if (isCurrentProjectBuildy) {
        return subCategories.filter((subCategory) => {
          if (selectedCategory.index === 2) {
            return subCategory.parentId === selectedCategory.parentId;
          }
          return subCategory.parentId === selectedCategory._id;
        });
      }
      return subCategories;
    });

    const [filteredSecondLevelCategories, setFilteredSecondLevelCategories] = useState(
      getAllCategories(categories).filter((category) => category.index === 1)
    );

    const [selectedSubCategory, setSelectedSubCategory] = useState(() => {
      if (isCurrentProjectBuildy) {
        return selectedCategory.index === 2 ? selectedCategory : null;
      }

      return selectedCategory.index === 1 ? selectedCategory : null;
    });

    const [selectedSecondLevelCategory, setSelectedSecondLevelCategory] = useState(
      selectedCategory.index === 1
        ? selectedCategory
        : getAllCategories(categories).filter((category) => {
            if (selectedCategory.index === 2) {
              return category._id === selectedCategory.parentId;
            }
            return category._id === selectedCategory._id;
          })[0]
    );

    const [selectedParentCategory, setSelectedParentCategory] = useState(
      selectedCategory.index === 0
        ? selectedCategory
        : categories.filter((category) => category._id === selectedSecondLevelCategory?.parentId)[0]
    );

    useImperativeHandle(ref, () => ({
      handleUpdate: () => {
        if (itemDraftState.categoryId) {
          onUpdate(itemDraftState);
        } else if (selectedSecondLevelCategory) {
          onUpdate({
            ...itemDraftState,
            categoryId: selectedSecondLevelCategory._id
          });
        } else {
          onUpdate({
            ...itemDraftState,
            categoryId: selectedParentCategory._id
          });
        }
      }
    }));

    const onImageDelete = (image) => {
      const newImages = selectedImages.filter((images) => images._id !== image._id);
      const newImagesIds = newImages.map((newImage) => {
        return newImage._id;
      });
      const deletedIds = [];
      deletedIds.push(...deletedImagesIds, image._id);
      dispatch(setItemsImagesDraft(newImages));
      dispatch(setItemDraft({ ...itemDraft, imageIds: newImagesIds, images: newImages }));
      setDeletedImagesIds(deletedIds);
    };
    const filterSubCategories = (categoryId) => {
      setFilteredSecondLevelCategories(
        secondLevelCategories.filter((subCategory) => subCategory.parentId === categoryId)
      );

      setFilteredSubCategories(
        getAllCategories(
          secondLevelCategories.filter((subCategory) => subCategory.parentId === categoryId)
        )
      );

      setSelectedSubCategory(null);
      setSelectedSecondLevelCategory(null);
    };

    const filterSecondLevelCategories = (categoryId) => {
      setFilteredSubCategories(
        getAllCategories(subCategories.filter((subCategory) => subCategory.parentId === categoryId))
      );
      setSelectedSubCategory(null);
    };

    const weights = ['0-1 kg', '1-5 kg', '5-10 kg', '10-20 kg', '20-50 kg', '50-100 kg', '100+ kg'];
    const genders = [
      { key: 'male', value: 'Male' },
      { key: 'female', value: 'Female' },
      { key: 'unisex', value: 'Unisex' },
      { key: 'girl', value: 'Girl' },
      { key: 'boy', value: 'Boy' }
    ];
    const conditions = ['New', 'Pre-owned', 'Vintage'];
    const cites = ['Tbilisi', 'Batumi', 'Gori', 'Kutaisi'];
    const onAddressSave = () => {
      dispatch(
        setItemDraft({
          ...itemDraft,
          address: itemAddressDraft
        })
      );
      setOpenModal(false);
    };

    return (
      <div ref={ref} className={classes.gridContainer}>
        <div className={classes.gridItem}>
          <CStandardInput
            defaultValue={title || '-'}
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-item-detail-title"
            id="standard-input-item-detail-title"
            label="Title"
            onChange={(event) => {
              setItemDraftState({ ...itemDraft, title: event.target.value });
            }}
            size="large"
            width="true"
          />
        </div>

        <div className={classes.gridItem}>
          <CStandardInput
            defaultValue={brand || '-'}
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-item-detail-brand"
            id="standard-input-item-detail-brand"
            label="Brand"
            onChange={(event) => setItemDraftState({ ...itemDraft, brand: event.target.value })}
            size="large"
            width="true"
          />
        </div>

        <div className={classes.carousel}>
          <Carousel autoplay autoplaySpeed={5000}>
            {selectedImages.map((image, index) => {
              return (
                <div key={index} className={classes.carouselWrapper}>
                  <img
                    key={image.url}
                    alt={`item number ${index}`}
                    className={classes.carouselImage}
                    src={image.url}
                  />
                  <p className={classes.carouselCancel} onClick={() => onImageDelete(image)}>
                    &#10006;
                  </p>
                </div>
              );
            })}
          </Carousel>
        </div>

        <div className={classes.gridItem}>
          <CStandardInput
            defaultValue={`${price} $` || '-'}
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-item-detail-price"
            id="standard-input-item-detail-price"
            label="Price"
            onChange={(event) => {
              setItemDraftState({
                ...itemDraft,
                price: Number.isNaN(
                  Number.parseFloat(event.target.value.substring(0, event.target.value - 2))
                )
                  ? 0
                  : Number.parseFloat(event.target.value.substring(0, event.target.value - 2))
              });
            }}
            size="large"
            width="true"
          />
        </div>

        <div className={classes.gridItem}>
          <p className={classes.selectHeader}>Gender</p>
          <DefaultSelect
            onSelect={(value) => setItemDraftState({ ...itemDraft, gender: value })}
            selectStyles={dropdownSelect}
            value={gender || ''}
          >
            {genders.map((gender) => {
              return (
                <MenuItem key={gender.key} value={gender.key}>
                  {gender.value}
                </MenuItem>
              );
            })}
          </DefaultSelect>
        </div>

        <div className={classes.gridItem}>
          <p className={classes.selectHeader}>Category</p>
          <DefaultSelect
            onSelect={(value) => {
              const selectedItem = categories.filter((category) => category.name === value)[0];
              filterSubCategories(selectedItem._id);
              setSelectedParentCategory(selectedItem);
              setItemDraftState({ ...itemDraftState, categoryId: '' });
            }}
            selectStyles={dropdownSelect}
            value={selectedParentCategory?.name || ''}
          >
            {categories.map((category) => {
              return (
                <MenuItem key={category._id} value={category.name}>
                  {category.name}
                </MenuItem>
              );
            })}
          </DefaultSelect>
        </div>

        <div className={classes.gridItem}>
          <p className={classes.selectHeader}>Condition</p>
          <DefaultSelect
            onSelect={(value) => setItemDraft({ ...itemDraft, condition: value })}
            selectStyles={dropdownSelect}
            value={condition || ''}
          >
            {conditions.map((condition) => {
              return (
                <MenuItem key={condition} value={condition}>
                  {condition}
                </MenuItem>
              );
            })}
          </DefaultSelect>
        </div>

        {isCurrentProjectBuildy ? (
          <>
            <div className={classes.gridItem}>
              <p className={classes.selectHeader}>Second level category</p>
              <DefaultSelect
                onSelect={(value) => {
                  const selectedSubCategory = filteredSecondLevelCategories.filter(
                    (subCategory) => subCategory.name === value
                  )[0];
                  filterSecondLevelCategories(selectedSubCategory._id);
                  setSelectedSecondLevelCategory(selectedSubCategory);
                  setSelectedParentCategory(
                    categories.filter(
                      (subCategory) => subCategory._id === selectedSubCategory.parentId
                    )[0]
                  );
                  setItemDraftState({ ...itemDraftState, categoryId: '' });
                }}
                selectStyles={dropdownSelect}
                value={selectedSecondLevelCategory?.name || ''}
              >
                {filteredSecondLevelCategories?.map((subCategory) => {
                  return (
                    <MenuItem key={subCategory._id} value={subCategory.name}>
                      {subCategory.name}
                    </MenuItem>
                  );
                })}
              </DefaultSelect>
            </div>
            <div className={classes.gridItem} />
          </>
        ) : null}

        <div className={classes.gridItem}>
          <p className={classes.selectHeader}>Sub-category</p>
          <DefaultSelect
            onSelect={(value) => {
              const selectedSubCategory = filteredSubCategories.filter(
                (subCategory) => subCategory.name === value
              )[0];

              setItemDraftState({
                ...itemDraft,
                categoryId: selectedSubCategory._id
              });
              setSelectedSubCategory(selectedSubCategory);
            }}
            selectStyles={dropdownSelect}
            value={selectedSubCategory?.name || ''}
          >
            {filteredSubCategories.map((subCategory) => {
              return (
                <MenuItem key={subCategory._id} value={subCategory.name}>
                  {subCategory.name}
                </MenuItem>
              );
            })}
          </DefaultSelect>
        </div>

        <div className={classes.gridItem}>
          <p className={classes.selectHeader}>Weight</p>
          <DefaultSelect
            onSelect={(value) => setItemDraftState({ ...itemDraft, weight: value })}
            selectStyles={dropdownSelect}
            value={weight || ''}
          >
            {weights.map((weight) => {
              return (
                <MenuItem key={weight} value={weight}>
                  {weight}
                </MenuItem>
              );
            })}
          </DefaultSelect>
        </div>
        <div className={classes.gridItem}>
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-item-detail-description"
            id="standard-input-item-detail-description"
            label="Description"
            multiline
            onChange={(event) => {
              setItemDraftState({ ...itemDraft, description: event.target.value });
            }}
            rows="3"
            size="large"
            value={itemDraftState.description || '-'}
            width="true"
          />
        </div>
        {stimullFee ? (
          <>
            <CStandardInput
              defaultValue={stimullFee || '10'}
              disableUnderline
              formclasses={{ root: classes.root }}
              htmlFor="standard-input-item-discounted-price"
              id="standard-input-item-discounted-price"
              label="Stimull Fee"
              onChange={(event) => {
                dispatch(
                  setItemDraft({
                    ...itemDraft,
                    metadata: {
                      ...itemDraft.metadata,
                      stimullFee: event.target.value
                    }
                  })
                );
              }}
              width="true"
            />
          </>
        ) : null}
        <div className={classes.gridItem} onClick={() => setOpenModal(true)}>
          <p className={classes.p}>Item Location</p>
          <span className={classes.span}>
            {`${address.city || '-'}, ${address.street || '-'}, ${address.apartment || '-'}`}
          </span>
        </div>

        <Modal
          footerActions={
            <>
              <CButton
                htmlType="submit"
                onClick={(e) => {
                  setOpenModal(false);
                  e.preventDefault();
                }}
                size="large"
                type="secondary"
              >
                Cancel
              </CButton>
              <CButton htmlType="submit" onClick={onAddressSave} size="large" type="primary">
                Save
              </CButton>
            </>
          }
          handleClose={() => setOpenModal(false)}
          isModalOpen={openModal}
          styles={{
            '& .MuiDialogContent-root': {
              paddingBottom: '24px'
            },
            width: '385px'
          }}
          titleContent={<span className="modal-title">Edit Address</span>}
        >
          <form className={classes.form} id="item-edit-form">
            <div className={classes.wrapperDiv}>
              <CStandardInput
                defaultValue={address?.name || '-'}
                disableUnderline
                formclasses={{ root: classes.root }}
                htmlFor="standard-item-edit-address"
                id="standard-item-edit-address"
                label="Full Name"
                onChange={(event) => {
                  itemAddressDraft.name = event.target.value;
                  dispatch(
                    setItemAddressDraft({
                      ...itemAddressDraft
                    })
                  );
                }}
                size="large"
                width="true"
              />

              <CStandardInput
                defaultValue={address?.street || '-'}
                disableUnderline
                formclasses={{ root: classes.root }}
                htmlFor="standard-item-edit-address"
                id="standard-item-edit-address"
                label="Address"
                onChange={(event) => {
                  itemAddressDraft.street = event.target.value;
                  dispatch(
                    setItemAddressDraft({
                      ...itemAddressDraft
                    })
                  );
                }}
                size="large"
                width="true"
              />

              <CStandardInput
                defaultValue={address?.apartment || '-'}
                disableUnderline
                formclasses={{ root: classes.root }}
                htmlFor="standard-item-edit-apartment"
                id="standard-item-edit-apartment"
                label="Apartment"
                onChange={(event) => {
                  itemAddressDraft.apartment = event.target.value;
                  dispatch(
                    setItemAddressDraft({
                      ...itemAddressDraft
                    })
                  );
                }}
                size="large"
                width="true"
              />

              <div>
                <p className={classes.selectHeader}>City</p>
                <DefaultSelect
                  onSelect={(value) => {
                    dispatch(
                      setItemAddressDraft({
                        ...itemAddressDraft,
                        city: value
                      })
                    );
                  }}
                  selectStyles={dropdownSelect}
                  value={itemAddressDraft?.city || ''}
                >
                  {cites.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </DefaultSelect>
              </div>

              <CStandardInput
                defaultValue={
                  `${address?.phone_number?.country_code} ${address?.phone_number?.number}` ?? '-'
                }
                disableUnderline
                formclasses={{ root: classes.root }}
                htmlFor="standard-item-edit-phone"
                id="standard-item-edit-phone"
                label="Phone Number"
                onChange={(event) => {
                  const [country_code, number] = event.target.value.split(' ');
                  itemAddressDraft.phone_number.number = number;

                  itemAddressDraft.phone_number.country_code = country_code;
                  dispatch(
                    setItemAddressDraft({
                      ...itemAddressDraft
                    })
                  );
                }}
                size="large"
                width="true"
              />

              <CStandardInput
                defaultValue={address?.additional_comment || ''}
                disableUnderline
                formclasses={{ root: classes.root }}
                htmlFor="standard-item-edit-comment"
                id="standard-item-edit-comment"
                label="Comment"
                onChange={(event) => {
                  itemAddressDraft.additional_comment = event.target.value;
                  dispatch(
                    setItemAddressDraft({
                      ...itemAddressDraft
                    })
                  );
                }}
                size="large"
                width="true"
              />
            </div>
          </form>
        </Modal>
      </div>
    );
  }
);
ItemDetailEdit.displayName = 'ItemDetailEdit';
export default ItemDetailEdit;
